import { useState, useMemo, useEffect } from 'react'
import { useRenewal } from '../../../context/renewal'
import {
  ApplicationBodyContainer, 
  ApplicationBodyContent,
  DefaultTitle, 
  DefaultSubtitle,
} from '../../../components/application/defaultComponents'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import PercentagesForm from 'src/components/application/mainPagesComponents/Firm/PercentagesForm'

interface AreasWithPercentage {
  area: string; //group
  type?: string;
  percentage: number;
  code?: string; // ISI code for this specific area+type combo.
}

const Percentages = () => {
  const { renewalData } = useRenewal()
  const [showAlert, setShowAlert] = useState(false)
  const [areasWithPercentage, setAreasWithPercentage] = useState<AreasWithPercentage[]>(() => {
    if(!renewalData?.firmSection?.practiceAreas) return []

    let data: AreasWithPercentage[] = []
    
    renewalData.firmSection.practiceAreas.forEach(area => {
      if(area.types && area.types.length > 0) {
        data = [...data, ...area.types.map(sub => ({ area: area.area ?? '', type: sub.type, percentage: sub.percentage ?? 0 }))]
      } else {
        data = [...data, { area: area.area ?? '', percentage: area.percentage ?? 0 }]
      }
    })

    return data
  })

  const totalPercentage = useMemo(() => {
    return areasWithPercentage.reduce((total, value) => parseFloat((total += value.percentage).toFixed(2)), 0)
  }, [areasWithPercentage])
  
  const handleSliderChange = (value: number, index: number) => {
    setAreasWithPercentage(prev => {
      const updated = [...prev]
      updated[index].percentage = value
      return updated
    })
  }

  const handleInputChange = (value: number, index: number) => {
    setAreasWithPercentage(prev => {
      const updated = [...prev]
      updated[index].percentage = (value ?? 0)/10
      return updated
    })
  }

  const buildPercentageData = () => {
    if(renewalData?.firmSection?.practiceAreas) {
      const practiceAreas = [...renewalData.firmSection.practiceAreas]

      return practiceAreas.map(pArea => {
        if(pArea?.types && pArea?.types.length > 0) {
          return {
            ...pArea,
            types: [
              ...pArea.types.map(tp => ({ ...tp, percentage: areasWithPercentage.find(ar => ar.type === tp.type)?.percentage ?? 0 }))
            ]
          }
        } else {
          return {
            ...pArea,
            percentage: areasWithPercentage.find(ar => ar.area === pArea.area)?.percentage ?? 0
          }
        }
      })
    }
    return []
  }

  const goNextRoute = () => {
    const area = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Real Estate')
    if (area !== undefined && area.types !== undefined && area.types.length > 0) {
      return '/customer/renew/firm/real-estate'
    }

    const sec1 = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt')
    const sec2 = renewalData.firmSection?.practiceAreas?.find(a => a.area === 'Securities - Other Types Of Securities Practice')

    if((sec1 || sec2) ){
      return '/customer/renew/firm/securities-law'
    }

    return '/customer/renew/attorneys/left'
  }

  useEffect(() => {
    if(totalPercentage !== 100 || areasWithPercentage.some(ar => ar.percentage === 0)) {
      setShowAlert(true)
    }
    else setShowAlert(false)
  }, [areasWithPercentage, totalPercentage])

  const validatePercentages = () => {
    const hasBuiltDataElements = buildPercentageData().length > 0

    const isPercentagesCorrectlyFilled = totalPercentage === 100 && areasWithPercentage.every(ar => ar.percentage > 0)

    return hasBuiltDataElements && isPercentagesCorrectlyFilled
  }

  const updatedApplicationData = () => ({
    ...renewalData,
    currentPage: window.location.pathname,
    firmSection: { ...renewalData.firmSection, practiceAreas: buildPercentageData() } 
  })

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width="94%">
        <DefaultTitle>Add percentages to your areas of practice.</DefaultTitle>
        <DefaultSubtitle>
          The total percentage must add up to 100%.
        </DefaultSubtitle>
        <PercentagesForm
          areasWithPercentage={areasWithPercentage}
          handleSliderChange={handleSliderChange}
          handleInputChange={handleInputChange}
          showAlert={showAlert}
          totalPercentage={totalPercentage}
        />
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/firm/practice"
        isFormValid={validatePercentages()}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
      />
    </ApplicationBodyContainer>
  )
}

export default Percentages